/**
 * Object KIND taxonomy (OBJECTS round, 2026-08 — docs/MODELER.md "OBJECT ANATOMY").
 * The kind is the single source the plan-symbol builder, the procedural 3D prototype,
 * and the settings modal all branch on — mirroring openingOps.ts for doors/windows.
 *
 * Every object = an AUTHORED parametric PLAN SYMBOL (a drafting convention, never a
 * mesh projection) + a 3D BODY. V1 bodies are PROCEDURAL prototypes; the type record
 * carries the reserved `meshRef` slot (schema.ts) so manufacturer/AI mesh attachment
 * arrives with the import round without schema rework — see the MESH INTAKE GATE
 * entry in docs/MODELER.md before building that round.
 *
 * V1 ships the curated kitchen + bath set, plus a laundry set added 2026-08-18.
 * Adding a kind later = a value here + a plan-symbol branch + a prototype branch +
 * a seeded type — never a schema reshape.
 *
 * PLACEMENT FRAME convention (shared by symbols, prototypes, footprints, handles):
 * the stored anchor is the object's BACK-CENTER (the wall side); local x runs across
 * the width centered on 0, local y = depth extending toward the FRONT (into the
 * room), rotationDeg = 0 faces world +y.
 */

import { PLUMBING_STYLES, plumbingStyleOf, plumbingWidthIn, bodyFinishOf } from './plumbingStyles'
import { wallHungRimIn, wallHungZSpanIn } from './wallHungToilet'
import { APPLIANCE_SIZED_KINDS, APPLIANCE_SIZES } from './applianceSizes'

export const OBJECT_KINDS = [
  // FAMILY CATEGORIES round (2026-08-26, Matt: "Objects need categories they can
  // be assigned... follow common revit family categories, for example Plumbing
  // Fixtures, Furniture, Etc."): `family` is the KIND's DEFAULT family — the
  // `familyCategory` type param (schema.ts) resolves to this when unset, and is
  // freely reassignable per type (families.ts#familyCategoryOf). Never a schema
  // reshape trigger — adding a kind still only needs one array entry.
  { value: 'kitchenSink', label: 'Kitchen Sink', family: 'Plumbing Fixtures' },
  { value: 'bathSink', label: 'Bath Sink (Lavatory)', family: 'Plumbing Fixtures' },
  { value: 'toilet', label: 'Toilet', family: 'Plumbing Fixtures' },
  // WALL-HUNG TOILET (2026-10-21, Matt): the bowl on an in-wall carrier with a concealed tank
  // and a flush plate — its own kind, since its rough-in is a different trade (wallHungToilet.ts)
  { value: 'wallHungToilet', label: 'Toilet — Wall-Hung (In-Wall Carrier)', family: 'Plumbing Fixtures' },
  { value: 'tub', label: 'Bathtub', family: 'Plumbing Fixtures' },
  // FREESTANDING TUB (2026-10-06, Matt: "a free-standing tub family in the plumbing
  // fitout"): a soaking tub standing in the room, with an optional floor-mounted filler
  { value: 'tubFreestanding', label: 'Bathtub — Freestanding', family: 'Plumbing Fixtures' },
  { value: 'shower', label: 'Shower', family: 'Plumbing Fixtures' },
  // SHOWER TRIM (2026-09-30, Matt: "add a shower faucet … two separate objects"):
  // the valve (trim plate + handle) and the showerhead (arm + head) place
  // independently — a head on another wall, or a hand shower, is its own object.
  // Both mount on the wall at `mountHeightFt` above their base.
  { value: 'showerValve', label: 'Shower Valve', family: 'Plumbing Fixtures' },
  { value: 'showerHead', label: 'Showerhead', family: 'Plumbing Fixtures' },
  // FIT-OUT CATALOG (2026-09-30, Matt): shower glass rides with the shower (it spans
  // the opening, so it is placed, never wall-hosted)
  { value: 'showerDoor', label: 'Shower Door (Glass)', family: 'Plumbing Fixtures' },
  { value: 'showerPanel', label: 'Shower Glass Panel (Fixed)', family: 'Plumbing Fixtures' },
  // PLUMBING STYLES (2026-10-07, Matt: faucets are their own fixtures, separate from the basin):
  // the trim the designers schedule separately — each a generic type with styles
  // (plumbingStyles.ts)
  { value: 'lavFaucet', label: 'Lavatory Faucet', family: 'Plumbing Fixtures' },
  { value: 'kitchenFaucet', label: 'Kitchen Faucet', family: 'Plumbing Fixtures' },
  { value: 'potFiller', label: 'Pot Filler', family: 'Plumbing Fixtures' },
  { value: 'tubFiller', label: 'Tub Filler', family: 'Plumbing Fixtures' },
  { value: 'handShower', label: 'Hand Shower', family: 'Plumbing Fixtures' },
  { value: 'range', label: 'Range', family: 'Appliances' },
  // COOKTOPS (2026-10-15, Armando: "a base cabinet with … an induction cooktop"): a cooktop
  // set INTO a countertop — gas, induction, electric, or a pro gas rangetop — its body hanging
  // below the counter, its top just proud of it (the sink precedent: it rides the counter it
  // is placed on and cuts its own opening)
  { value: 'cooktop', label: 'Cooktop', family: 'Appliances' },
  { value: 'fridge', label: 'Refrigerator', family: 'Appliances' },
  // KITCHEN APPLIANCES round (2026-10-01, Matt): an integrated COLUMN refrigerator
  // (Sub-Zero style — one full-height panel door, flush with the cabinets; preset
  // widths as types, any size per instance) and a HOOD INSERT for the range (it hangs
  // in its enclosure at a settable height above the level, drawn dashed in plan)
  { value: 'columnFridge', label: 'Column Refrigerator', family: 'Appliances' },
  { value: 'hoodInsert', label: 'Hood Insert', family: 'Appliances' },
  { value: 'dishwasher', label: 'Dishwasher', family: 'Appliances' },
  // DERIVED HOOKUPS round (2026-10-04): the equipment the hookup list called for that
  // the library didn't have yet
  { value: 'microwaveDrawer', label: 'Microwave Drawer', family: 'Appliances' },
  { value: 'wallOven', label: 'Wall Oven', family: 'Appliances' },
  { value: 'beverageCenter', label: 'Wine / Beverage Fridge', family: 'Appliances' },
  { value: 'iceMaker', label: 'Ice Maker', family: 'Appliances' },
  // CASEWORK STUDIO stage B (2026-10-09): a BUILT-IN appliance — one kind, its look per
  // instance (`applianceUnit`, caseworkAppliances.ts), set into a cabinet opening the Studio
  // builds around it at its true size
  { value: 'builtInAppliance', label: 'Built-In Appliance', family: 'Appliances' },
  // OUTDOOR GRILLS (2026-10-08): one Grill kind, its look a per-instance Style (plumbingStyles.ts)
  { value: 'outdoorGrill', label: 'Grill', family: 'Outdoor Cooking' },
  { value: 'laundrySink', label: 'Laundry Sink', family: 'Plumbing Fixtures' },
  { value: 'hoseBib', label: 'Hose Bib', family: 'Plumbing Fixtures' },
  { value: 'washer', label: 'Laundry Washer', family: 'Appliances' },
  { value: 'dryer', label: 'Laundry Dryer', family: 'Appliances' },
  { value: 'washerDryerCombo', label: 'Combo Stacked Washer/Dryer', family: 'Appliances' },
  // FIT-OUT CATALOG: fireplaces and stoves live with the appliances for now (Matt)
  { value: 'fireplaceLinear', label: 'Linear Gas Fireplace', family: 'Appliances' },
  { value: 'fireplaceWood', label: 'Wood-Burning Fireplace', family: 'Appliances' },
  { value: 'woodStove', label: 'Wood Stove', family: 'Appliances' },
  // FIT-OUT CATALOG: mechanical / HVAC lands under Equipment (Matt) — the family
  // the Fit-Out panel's Equipment button narrows to
  { value: 'waterHeater', label: 'Water Heater (Tank)', family: 'Specialty Equipment' },
  { value: 'tanklessWaterHeater', label: 'Tankless Water Heater', family: 'Specialty Equipment' },
  { value: 'furnace', label: 'Furnace', family: 'Specialty Equipment' },
  { value: 'airHandler', label: 'Air Handler / Fan Coil', family: 'Specialty Equipment' },
  { value: 'condenser', label: 'AC Condenser / Heat Pump', family: 'Specialty Equipment' },
  { value: 'miniSplitHead', label: 'Mini-Split Wall Unit', family: 'Specialty Equipment' },
  { value: 'miniSplitOutdoor', label: 'Mini-Split Outdoor Unit', family: 'Specialty Equipment' },
  { value: 'boiler', label: 'Wall-Hung Boiler', family: 'Specialty Equipment' },
  { value: 'floorRegister', label: 'Floor Register', family: 'Specialty Equipment' },
  // POOL DESIGNER round (2026-10-02): the pool equipment pad — generic pump, filter,
  // heater and controls on a slab, placed anywhere on the site, any size
  { value: 'poolEquipment', label: 'Pool Equipment Pad', family: 'Specialty Equipment' },
  // RESIDENTIAL ELEVATOR (2026-10-02, Matt: "an elevator family under equipment that I
  // can place and choose where the top stop is … a cab and rails … residential style"):
  // the hoistway footprint is the object's size; the cab rides two guide rails at the
  // back from its own level (the bottom stop) to the picked top-stop level
  { value: 'elevator', label: 'Residential Elevator', family: 'Specialty Equipment' },
  { value: 'returnGrille', label: 'Wall Return Grille', family: 'Specialty Equipment' },
  // FURNITURE (2026-09-30, Matt): generic SCHEMATIC furniture — sized per INSTANCE
  // (FURNITURE_SIZED_KINDS below), the bed by mattress size + a frame allowance
  { value: 'sofa', label: 'Sofa', family: 'Furniture' },
  { value: 'sectional', label: 'Sectional Sofa', family: 'Furniture' },
  { value: 'bed', label: 'Bed', family: 'Furniture' },
  { value: 'dresser', label: 'Dresser', family: 'Furniture' },
  { value: 'nightstand', label: 'Nightstand', family: 'Furniture' },
  { value: 'coffeeTable', label: 'Coffee Table', family: 'Furniture' },
  { value: 'diningTable', label: 'Dining Table', family: 'Furniture' },
  { value: 'diningChair', label: 'Dining Chair', family: 'Furniture' },
  { value: 'barStool', label: 'Bar Stool', family: 'Furniture' },
  { value: 'desk', label: 'Desk', family: 'Furniture' },
  { value: 'deskChair', label: 'Desk Chair', family: 'Furniture' },
  // RUGS (2026-10-01, Matt): an area rug and a RUNNER (long and skinny, for entries and
  // halls) — sized per instance like the rest of the furniture
  { value: 'areaRug', label: 'Area Rug', family: 'Furniture' },
  { value: 'runner', label: 'Runner (Rug)', family: 'Furniture' },
  // LIVING + GAME ROOM (2026-10-01, Matt: "a lounge chair (arm chair for a living
  // room), a wall-mounted TV, a pool table, and a ping-pong (table tennis) table")
  { value: 'loungeChair', label: 'Lounge Chair (Armchair)', family: 'Furniture' },
  { value: 'poolTable', label: 'Pool Table', family: 'Furniture' },
  { value: 'pingPongTable', label: 'Ping-Pong Table', family: 'Furniture' },
  { value: 'wallTV', label: 'Wall-Mounted TV', family: 'Appliances' },
  // VEHICLES (2026-10-01, Matt: "a few cars to be able to put in driveways and garages")
  // — Revit's Entourage category; the Fit-Out panel's Vehicles button narrows to it
  { value: 'carSedan', label: 'Car — Sedan', family: 'Entourage' },
  { value: 'carSUV', label: 'Car — SUV', family: 'Entourage' },
  // SITE FURNISHINGS (2026-10-06, Matt: "a new trampoline family object … under the site
  // ribbon") — yard pieces; the Site tab's Fit-Out → Site Furnishings button narrows to it
  { value: 'trampoline', label: 'Trampoline', family: 'Site Furnishings' },
  // (2026-10-06, Matt: "a rectangular format trampoline, too") — width × depth, rounded corners
  { value: 'trampolineRect', label: 'Trampoline — Rectangular', family: 'Site Furnishings' },
  // FIRE PIT (2026-10-19, Matt: "a fire pit family … length and width as instance
  // parameters"): a rectangular gas fire pit — stone body, 3" polished concrete cap, fire
  // media in the burner opening. Length / width / height are per placement (FIRE_PIT_*)
  { value: 'firePit', label: 'Fire Pit', family: 'Site Furnishings' },
  // BUBBLER FOUNTAIN (2026-10-19, Matt: "Now a round limestone fountain"): a hand-formed
  // round drum with its bubbler - diameter and height per placement, placed at its centre
  { value: 'fountain', label: 'Fountain — Round Bubbler', family: 'Site Furnishings' },
  // YARD PIECES (2026-10-19, Matt: "typical residential trash bins in standard sizes … patio
  // umbrellas (one rectangular and one typical multisided) and lounge chaise"): the city
  // wheeled cart (sizes are types: 32 / 64 / 96 gal), the octagonal and rectangular market
  // umbrellas, and the poolside chaise — bim-geometry object/yardPieces.ts
  { value: 'trashCart', label: 'Trash Cart', family: 'Site Furnishings' },
  { value: 'umbrellaOct', label: 'Patio Umbrella — Octagonal', family: 'Site Furnishings' },
  { value: 'umbrellaRect', label: 'Patio Umbrella — Rectangular', family: 'Site Furnishings' },
  { value: 'chaise', label: 'Chaise Lounge', family: 'Site Furnishings' },
  // SITE UTILITIES (2026-10-07, Matt: "a typical residential gas meter … to a new Site
  // Utilities category … Also add an in-ground water meter in a yard box") — the service
  // entrances; the Site tab's Fit-Out → Site Utilities button narrows to them
  { value: 'gasMeter', label: 'Gas Meter Set', family: 'Site Utilities' },
  { value: 'waterMeterBox', label: 'Water Meter Box', family: 'Site Utilities' },
  // PLANTING (2026-10-04, Matt: stylized plants that sway in the breeze — Revit's
  // Planting category, its own Fit-Out button): one kind, the
  // species picked by the `plantSpecies` type param (plantSpecies.ts). The anchor is
  // still back-center by convention; the plant is centered in its width × depth
  // footprint, and placement offsets the anchor so the click lands on the plant's center
  { value: 'plant', label: 'Plant', family: 'Planting' },
  // PHOTO-TO-3D round (docs/bim-plan/17, Matt's resolved Q3: option (a)): the
  // sentinel kind for photo/mesh-derived firm-library types. Never seeded (the
  // seed-roster test excludes it — these types only ever come from the capture →
  // review → bake flow in the firm-library editor), no procedural body branch
  // (the body IS the imported mesh via `meshRef`; an unregistered mesh degrades
  // to the default box), no authored symbol branch (the plan symbol is the BAKED
  // top-down silhouette stored in the type's `planSymbol` param). Kind-based
  // conditionals (basins/rimHeightFt) all list real kinds, so they hide here
  // automatically. Defaults to Furniture (the common photo-capture subject —
  // chairs, tables, decor) — always reassignable per type since a captured mesh
  // could be anything.
  { value: 'imported', label: 'Imported (Photo / Mesh)', family: 'Furniture' },
] as const

export type ObjectKind = (typeof OBJECT_KINDS)[number]['value']

export function objectKindLabel(kind: string | undefined): string {
  return OBJECT_KINDS.find((k) => k.value === kind)?.label || 'Object'
}

/** FURNITURE (2026-09-30): kinds whose width/depth/height are INSTANCE params
 *  (`sizeWidthFt`/`sizeDepthFt`/`sizeHeightFt`, 0 = the type's) — one type, any size.
 *  The bed is sized by `bedSize` instead (BED_SIZES), with only its height free. */
export const FURNITURE_SIZED_KINDS: readonly string[] = [
  'sofa', 'sectional', 'dresser', 'nightstand', 'coffeeTable', 'diningTable',
  'diningChair', 'barStool', 'desk', 'deskChair', 'areaRug', 'runner',
  // LIVING + GAME ROOM round
  'loungeChair', 'poolTable', 'pingPongTable',
  // KITCHEN APPLIANCES round: preset column widths are types, any size per instance
  'columnFridge',
  // POOL DESIGNER round: the equipment pad is sized per placement
  'poolEquipment',
  // RESIDENTIAL ELEVATOR: the hoistway size is per placement too
  'elevator',
  // CASEWORK STUDIO stage B: a built-in appliance is the size of its opening
  'builtInAppliance',
  // STANDARD APPLIANCE SIZES (2026-10-15): every standalone appliance with standard sizes
  // (applianceSizes.ts) is sized per placement too
  ...APPLIANCE_SIZED_KINDS.filter((k) => k !== 'columnFridge'),
  // YARD PIECES: an umbrella's canopy and a chaise are sized per placement (the octagon's
  // Width is its diameter — applyObjectInstanceSize keeps its depth equal)
  'umbrellaOct', 'umbrellaRect', 'chaise',
]

/** KITCHEN APPLIANCES round: range cooktop options (the Wolf dual-fuel lineup's
 *  configurations — burners plus an infrared griddle and/or charbroiler) */
export const RANGE_COOKTOP_EXTRAS = [
  { value: 'none', label: 'Burners only' },
  { value: 'griddle', label: 'Griddle' },
  { value: 'charbroiler', label: 'Charbroiler' },
  { value: 'griddleCharbroiler', label: 'Griddle + Charbroiler' },
] as const
/** the hood insert's default bottom above the level — 30" over a 36" cooktop */
export const HOOD_BOTTOM_DEFAULT_FT = 5.5
/** kinds that must never auto-host onto the surface under them (the hood hangs over
 *  the range, it does not sit on it) */
export const UNHOSTED_OBJECT_KINDS: readonly string[] = ['hoodInsert']

/** kinds that sit on the TERRAIN when there is no slab under them (a plant in the yard,
 *  a gas meter set on the ground beside the house, a water meter box flush with grade) */
export const TERRAIN_HOSTED_OBJECT_KINDS: readonly string[] = ['plant', 'gasMeter', 'waterMeterBox', 'firePit', 'fountain', 'trashCart', 'umbrellaOct', 'umbrellaRect', 'chaise']

/** YARD PIECES (2026-10-19): the colours a trash cart, an umbrella canopy and a chaise come in */
export const BIN_STREAMS = [
  { value: 'trash', label: 'Trash (black)', hex: '#2f3133' },
  { value: 'recycling', label: 'Recycling (blue)', hex: '#2f5f9e' },
  { value: 'organics', label: 'Green waste (green)', hex: '#3f7a3a' },
  { value: 'gray', label: 'Gray', hex: '#7e8285' },
] as const
export const FABRIC_COLORS = [
  { value: 'natural', label: 'Natural', hex: '#d9cfb8' },
  { value: 'white', label: 'White', hex: '#f0eee8' },
  { value: 'navy', label: 'Navy', hex: '#2c3e5c' },
  { value: 'forest', label: 'Forest green', hex: '#3e5a40' },
  { value: 'charcoal', label: 'Charcoal', hex: '#4a4c4e' },
  { value: 'terracotta', label: 'Terracotta', hex: '#b8674a' },
] as const
export const CHAISE_FRAMES = [
  { value: 'teak', label: 'Teak', hex: '#9a6b43' },
  { value: 'black', label: 'Black aluminum', hex: '#2b2c2d' },
  { value: 'white', label: 'White aluminum', hex: '#e8e8e4' },
] as const
export const CHAISE_BACKS = [
  { value: 'reclined', label: 'Reclined', deg: 40 },
  { value: 'upright', label: 'Upright', deg: 65 },
  { value: 'flat', label: 'Flat', deg: 4 },
] as const

/** FIRE PIT (2026-10-19): the media in the burner opening */
export const FIRE_PIT_MEDIA = [
  { value: 'balls', label: 'Fire balls' },
  { value: 'glass', label: 'Fire glass' },
  { value: 'none', label: 'None (burner only)' },
] as const
/** FIRE PIT: the cap's thickness and its overhang past the stone (inches) */
export const FIRE_PIT_CAP_IN = 3
export const FIRE_PIT_CAP_OVERHANG_IN = 1
/** FIRE PIT: the cap's usual band width round the burner opening (inches) */
export const FIRE_PIT_CAP_BAND_IN = 10

/** RUGS: kinds that lie flat on the floor UNDER other furniture — never a host for
 *  another object (a sofa set on a rug sits on the floor), and they lose plan picks
 *  to whatever stands on them */
export const RUG_KINDS: readonly string[] = ['areaRug', 'runner']

/**
 * PLACEMENT SIZE (2026-10-01): an object's per-INSTANCE size folded into its type
 * params — the one rule `resolveElement` applies to a placed object AND the placement
 * ghost applies to the object about to be placed, so the symbol you aim with is the
 * size that lands. Mutates `tp` and `params` (pass copies). Sized furniture/rugs read
 * `sizeWidthFt`/`sizeDepthFt`/`sizeHeightFt` (0 = the type's), the bed derives its
 * footprint from the mattress size + frame allowance, a shower its pan size.
 */
export function applyObjectInstanceSize(tp: Record<string, unknown>, params: Record<string, unknown>): void {
  const kind = String(tp.objectKind || '')
  // STANDARD APPLIANCE SIZES: a picked size with no dimensions written yet (the settings for
  // the next appliance placed) supplies them
  const picked = APPLIANCE_SIZES[kind]?.find((s) => s.value === params.applianceSize)
  if (picked) {
    for (const [inst, inch] of [['sizeWidthFt', picked.w], ['sizeDepthFt', picked.d], ['sizeHeightFt', picked.h]] as const) {
      if (!(typeof params[inst] === 'number' && (params[inst] as number) > 0)) params[inst] = inch / 12
    }
  }
  if (FURNITURE_SIZED_KINDS.includes(kind) || kind === 'bed') {

    const pairs = kind === 'bed'
      ? [['sizeHeightFt', 'heightFt']] as const
      : [['sizeWidthFt', 'widthFt'], ['sizeDepthFt', 'depthFt'], ['sizeHeightFt', 'heightFt']] as const
    for (const [inst, key] of pairs) {
      const v = params[inst]
      if (typeof v === 'number' && v > 0) { tp[key] = v; params[key] = v }
      else params[inst] = (tp[key] as number) || 0
    }
  }
  // FIRE PIT (2026-10-19): Length (along the long side, the object's width), Width (front
  // to back, its depth) and Height per placement — 0 = the type's — plus the media and the
  // stone / cap materials, so the 3D body and its instancing key follow each placement
  if (kind === 'firePit') {
    for (const [inst, key] of [['pitLengthFt', 'widthFt'], ['pitWidthFt', 'depthFt'], ['pitHeightFt', 'heightFt']] as const) {
      const v = params[inst]
      if (typeof v === 'number' && v > 0) { tp[key] = v; params[key] = v }
      else params[inst] = (tp[key] as number) || 0
    }
    const media = String(params.pitMedia || tp.pitMedia || '')
    tp.pitMedia = FIRE_PIT_MEDIA.some((m) => m.value === media) ? media : 'balls'
    for (const key of ['pitStoneMaterial', 'pitCapMaterial'] as const) {
      const v = params[key]
      if (typeof v === 'string' && v) tp[key] = v
    }
  }
  // BUBBLER FOUNTAIN (2026-10-19): Diameter (both the width and the depth) and Height per
  // placement - 0 = the type's - plus its finish material
  if (kind === 'fountain') {
    const dia = params.fountainDiameterFt
    if (typeof dia === 'number' && dia > 0) { tp.widthFt = dia; tp.depthFt = dia; params.widthFt = dia; params.depthFt = dia }
    else params.fountainDiameterFt = Math.min((tp.widthFt as number) || 3.5, (tp.depthFt as number) || 3.5)
    const fh = params.fountainHeightFt
    if (typeof fh === 'number' && fh > 0) { tp.heightFt = fh; params.heightFt = fh }
    else params.fountainHeightFt = (tp.heightFt as number) || 0
    if (typeof params.fountainMaterial === 'string' && params.fountainMaterial) tp.fountainMaterial = params.fountainMaterial
  }
  // YARD PIECES (2026-10-19): an octagonal canopy is round — its Width is the diameter, so
  // its depth follows; each piece's colours (and the chaise's back) are per placement
  if (kind === 'umbrellaOct') { tp.depthFt = tp.widthFt; params.depthFt = tp.widthFt }
  const pick = (key: string, list: readonly { value: string }[]) => {
    const v = String(params[key] || tp[key] || '')
    if (list.some((c) => c.value === v)) tp[key] = v
    else delete tp[key]
  }
  if (kind === 'trashCart') pick('binStream', BIN_STREAMS)
  if (kind === 'umbrellaOct' || kind === 'umbrellaRect') pick('canopyColor', FABRIC_COLORS)
  if (kind === 'chaise') { pick('cushionColor', FABRIC_COLORS); pick('chaiseFrame', CHAISE_FRAMES); pick('chaiseBack', CHAISE_BACKS) }
  if (kind === 'sectional') tp.sectionalHand = params.sectionalHand === 'right' ? 'right' : 'left'
  // STANDARD APPLIANCE SIZES: no pick yet (an older placement, or a typed size) — show the
  // standard size it already IS, if any, so the pick reads right
  const sizes = APPLIANCE_SIZES[kind]
  if (sizes && !params.applianceSize) {
    const near = (a: unknown, inch: number) => Math.abs(((a as number) || 0) * 12 - inch) < 0.13
    const hit = sizes.find((s) => near(tp.widthFt, s.w) && near(tp.depthFt, s.d) && near(tp.heightFt, s.h))
    if (hit) params.applianceSize = hit.value
  }

  // SHOWER DOOR FLIP: per-instance hinge + swing side — folded into the type params so
  // the plan symbol and the 3D body (and its instancing key) both flip
  // COLUMN FRIDGE HINGE (2026-10-01, Matt: "flip the door swing direction on the fridge
  // columns"): the panel door's hinge side, per placement (left = the original drawing)
  if (kind === 'columnFridge') tp.fridgeHinge = params.fridgeHinge === 'right' ? 'right' : 'left'
  // COOKTOPS: the style, burner count and griddle / charbroiler are per placement
  if (kind === 'cooktop') {
    tp.cooktopStyle = cooktopStyleOf({ cooktopStyle: params.cooktopStyle ?? tp.cooktopStyle })
    const n = typeof params.cooktopBurners === 'number' && params.cooktopBurners > 0 ? Math.round(params.cooktopBurners) : 0
    tp.burners = n || cooktopUsualBurners(tp)
    const zone = String(params.cooktopZone || 'none')
    tp.cooktopExtra = isGlassCooktop(tp) ? 'none' : (RANGE_COOKTOP_EXTRAS.some((c) => c.value === zone) ? zone : 'none')
  }
  // CASEWORK STUDIO stage B: the built-in appliance's look (a unit of caseworkAppliances.ts)
  if (kind === 'builtInAppliance') {
    if (typeof params.applianceUnit === 'string' && params.applianceUnit) tp.applianceUnit = params.applianceUnit
    if (typeof params.applianceKind === 'string' && params.applianceKind) tp.applianceKind = params.applianceKind
    tp.applianceGrille = params.applianceGrille === true
  }
  if (kind === 'showerDoor') {
    tp.showerDoorHinge = params.showerDoorHinge === 'right' ? 'right' : 'left'
    tp.showerDoorSwing = params.showerDoorSwing === 'in' ? 'in' : 'out'
  }
  // LIVING + GAME ROOM round: the tables' dashed PLAY CLEARANCE is per instance
  if (kind === 'poolTable' || kind === 'pingPongTable') tp.playClearance = params.playClearance === true
  if (kind === 'bed') {
    const size = BED_SIZES.find((b) => b.value === params.bedSize) ?? BED_SIZES.find((b) => b.value === 'queen')!
    const num = (v: unknown, d: number) => (typeof v === 'number' && v >= 0 ? v : d)
    const m = num(params.frameMarginIn, BED_FRAME_MARGIN_IN)
    const hb = num(params.headboardIn, BED_HEADBOARD_IN)
    tp.bedSize = size.value
    tp.mattressWidthFt = size.w / 12
    tp.mattressDepthFt = size.d / 12
    tp.frameMarginFt = m / 12
    tp.headboardFt = hb / 12
    tp.widthFt = (size.w + 2 * m) / 12
    tp.depthFt = (size.d + m + hb) / 12
    params.widthFt = tp.widthFt
    params.depthFt = tp.depthFt
  }
  // KITCHEN APPLIANCES round: the hood's own "height above level" IS its instance
  // base (heightOffsetFt is hidden for it — one height field, never two)
  if (kind === 'hoodInsert') {
    const v = params.hoodBottomFt
    params.hoodBottomFt = typeof v === 'number' && v > 0 ? v : HOOD_BOTTOM_DEFAULT_FT
    params.heightOffsetFt = params.hoodBottomFt
  }
  // PLUMBING STYLES (2026-10-07): a styled fixture is the size of its STYLE (and its picked
  // size) — folded into the resolved type params so the footprint, the pick box, hosting, the
  // plan and the instancing key all follow it
  const ps = PLUMBING_STYLES[kind]
  if (ps) {
    // the instance's pick wins; a type may carry a default style / size / body finish
    const merged = { ...tp, ...params }
    const st = plumbingStyleOf(kind, merged)!
    tp[ps.param] = st.value
    params[ps.param] = st.value
    const wIn = plumbingWidthIn(st, merged)
    tp.fixtureSizeIn = String(wIn)
    tp.bodyFinish = bodyFinishOf(st, merged).value
    tp.widthFt = wIn / 12
    tp.depthFt = st.d / 12
    tp.heightFt = (st.z1 - st.z0) / 12
    params.widthFt = tp.widthFt
    params.depthFt = tp.depthFt
    if (st.mountFt && !((tp.mountHeightFt as number) > 0)) tp.mountHeightFt = st.mountFt
    // WALL-HUNG TOILET: the rim height is per placement (the carrier's feet), and the whole
    // fixture's height follows it
    if (kind === 'wallHungToilet') {
      const rim = wallHungRimIn(merged)
      tp.wallHungRimFt = rim / 12
      params.wallHungRimFt = rim / 12
      const z = wallHungZSpanIn(rim)
      tp.heightFt = (z.z1 - z.z0) / 12
    }
  }
  if (kind === 'shower') {
    for (const [inst, key] of [['panWidthFt', 'widthFt'], ['panDepthFt', 'depthFt']] as const) {
      const v = params[inst]
      if (typeof v === 'number' && v > 0) { tp[key] = v; params[key] = v }
      else params[inst] = (tp[key] as number) || 0
    }
  }
}

/** PLACEMENT SIZE: the instance params that size an object — a pre-set value for one
 *  TYPE never carries to another (a sofa's 7' must not shrink the next sectional) */
export const OBJECT_SIZE_PARAM_KEYS: readonly string[] = [
  'sizeWidthFt', 'sizeDepthFt', 'sizeHeightFt', 'panWidthFt', 'panDepthFt', 'applianceSize',
  'pitLengthFt', 'pitWidthFt', 'pitHeightFt', 'fountainDiameterFt', 'fountainHeightFt',
]


/** FURNITURE: standard US MATTRESS sizes in inches (width × length). The bed's
 *  footprint is the mattress plus its FRAME — `frameMarginIn` at both sides and the
 *  foot, `headboardIn` at the head — so a room is checked against the real bed. */
export const BED_SIZES = [
  { value: 'twin', label: 'Twin (38" × 75")', w: 38, d: 75 },
  { value: 'twinXL', label: 'Twin XL (38" × 80")', w: 38, d: 80 },
  { value: 'full', label: 'Full (54" × 75")', w: 54, d: 75 },
  { value: 'queen', label: 'Queen (60" × 80")', w: 60, d: 80 },
  { value: 'king', label: 'King (76" × 80")', w: 76, d: 80 },
  { value: 'calKing', label: 'California King (72" × 84")', w: 72, d: 84 },
] as const
export const BED_FRAME_MARGIN_IN = 2
export const BED_HEADBOARD_IN = 3

/** SHOWER TRIM: wall-mounted kinds — `mountHeightFt` (the valve's CENTER, the
 *  showerhead's arm) above the object's base, with the firm default per kind. */
export const OBJECT_KINDS_WALL_MOUNT: readonly string[] = [
  'showerValve', 'showerHead', 'tanklessWaterHeater', 'miniSplitHead', 'boiler', 'returnGrille',
  'wallTV', 'wallOven', 'hoseBib',
  // PLUMBING STYLES (2026-10-07)
  'potFiller', 'handShower',
]
export const WALL_MOUNT_DEFAULT_FT: Record<string, number> = {
  showerValve: 4, showerHead: 6 + 8 / 12,
  // FIT-OUT CATALOG (to the unit's CENTER): typical install heights
  tanklessWaterHeater: 5, miniSplitHead: 7, boiler: 5, returnGrille: 1.5,
  // LIVING + GAME ROOM round: a living-room TV's center at 4'-0" (the seated-eye
  // standard; raise it per type for over a fireplace)
  wallTV: 4,
  // DERIVED HOOKUPS round: a wall oven's center in its tall cabinet; a hose bib's spout
  wallOven: 4, hoseBib: 1.5,
  // PLUMBING STYLES: a pot filler 57" (about 21" over a 36" cooktop); a slide bar's foot at 39"
  potFiller: 4.75, handShower: 3.25,
}

/** LIVING + GAME ROOM round: the play-clearance band each table draws when switched on
 *  (feet beyond the table edge, ends × sides) — pool: a 58" cue plus the stroke all
 *  round; table tennis: the recreational play area (19' × 11' around a 9' × 5' table) */
export const PLAY_CLEARANCE_FT: Record<string, { ends: number; sides: number }> = {
  poolTable: { ends: 5, sides: 5 },
  pingPongTable: { ends: 5, sides: 3 },
}

/** Kinds whose rim/deck rides a counter or vanity height — `rimHeightFt` applies. */
export const OBJECT_KINDS_WITH_RIM: readonly string[] = ['kitchenSink', 'bathSink', 'lavFaucet', 'kitchenFaucet', 'cooktop']

/** COOKTOPS (2026-10-15): the cooktop's style — the look, its burners or zones, and its
 *  hookups (gas + 120V, or a 240V circuit) */
export const COOKTOP_STYLES = [
  { value: 'gas', label: 'Gas (grates over sealed burners)' },
  { value: 'induction', label: 'Induction (black glass)' },
  { value: 'electric', label: 'Electric radiant (glass)' },
  { value: 'rangetop', label: 'Gas rangetop (pro, front knobs)' },
] as const
export const cooktopStyleOf = (p: Record<string, unknown>): string => {
  const v = String(p.cooktopStyle || '')
  return COOKTOP_STYLES.some((s) => s.value === v) ? v : 'gas'
}
/** a glass-top cooktop (induction / radiant) — flush, zones instead of grates */
export const isGlassCooktop = (p: Record<string, unknown>): boolean => {
  const s = cooktopStyleOf(p)
  return s === 'induction' || s === 'electric'
}
/** the usual burner / zone count for a cooktop's width: 24" 4 · 30" 4 · 36" 5 · 48" 6 */
export function cooktopUsualBurners(p: Record<string, unknown>): number {
  const wIn = (((p.widthFt as number) || 2.5) * 12)
  if (wIn >= 46) return 6
  if (wIn >= 34) return 5
  return 4
}



/** FLOATING FURNITURE FIX (2026-10-01, Matt: "the coffee table floats off its set
 *  level"): the ONLY kinds that sit ON a counter or another object — the sinks that
 *  drop into a countertop. Every other object (furniture, appliances, fixtures) stands
 *  on the FLOOR: it hosts onto a floor slab or nothing, never onto the casework or
 *  sofa its anchor happens to fall inside (a coffee table set close in front of a
 *  sofa used to ride up onto the sofa's top). */
export const SURFACE_SITTING_OBJECT_KINDS: readonly string[] = [
  ...OBJECT_KINDS_WITH_RIM,
  // PLUMBING STYLES: a tub filler stands on the floor beside a tub — or on the tub's own rim
  // (the clawfoot telephone mixer)
  'tubFiller',
  // OUTDOOR GRILLS: a built-in head drops into an outdoor kitchen counter
  'outdoorGrill',
]

/** PHOTO-TO-3D round: kinds that are never seeded — they only ever come from an
 *  authoring flow (photo import), so the "one firm-standard seed per kind"
 *  roster invariant deliberately skips them. */
export const UNSEEDED_OBJECT_KINDS: readonly string[] = ['imported']

// CEILING HOSTING round (2026-08-23, Matt: "Lights should be under electrical
// fixtures, not objects... We already have sconces there."): recessedCan/pendant/
// flushMount — and LIGHT_OBJECT_KINDS/CEILING_HEIGHT_STANDARD_FT/
// RECESSED_TRIM_SHAPES that went with them — moved to categories/fixture/
// fixtureKinds.ts (CEILING_FIXTURE_KINDS). Round C's "freestanding, no hosting
// work" call (docs/bim-plan/18 §6 Q3) is reversed: these now host into a real
// ceiling element, so CEILING_HEIGHT_STANDARD_FT's flat guessed default is gone
// too — height derives from the actual host ceiling now (fixture/generate3d.ts).
